import React from "react";
import MobileCardsTemplate from "../templates/MobileCardsTemplate";
import { fromDateOnlyString } from "../../shared/dateOnly";

export interface ExpenseListItem {
  id: number;
  vendor?: string | null;
  vendor_address?: string | null;
  date?: string | null;
  total_amount?: string | number | null;
  receipt_path?: string | null;
}

interface ExpenseActions {
  onView: (expense: ExpenseListItem) => void;
  onDelete: (expense: ExpenseListItem) => void;
}

interface MobileCardsTemplateProps {
  data: ExpenseListItem[];
  onCardClick: (expense: ExpenseListItem) => void;
  emptyMessage: string;
  cardContent: (expense: ExpenseListItem) => React.ReactNode;
  actionButtons: (expense: ExpenseListItem) => React.ReactNode;
}

const ExpenseMobileCardsTemplate =
  MobileCardsTemplate as unknown as React.ComponentType<MobileCardsTemplateProps>;

export function formatExpenseDate(value?: string | null): string {
  if (!value) return "—";
  const date = fromDateOnlyString(value) ?? new Date(value);
  if (Number.isNaN(date.getTime())) return value;

  return date.toLocaleDateString(undefined, {
    month: "short",
    day: "numeric",
    year: "numeric",
  });
}

export function createExpenseColumns({ onView, onDelete }: ExpenseActions) {
  return [
    {
      field: "vendor",
      header: "Vendor",
      style: { width: "25%" },
      body: (expense: ExpenseListItem) => (
        <span className="font-semibold apollo-subheading text-gray-900">
          {expense.vendor || "—"}
        </span>
      ),
    },
    {
      field: "vendor_address",
      header: "Address",
      style: { width: "25%" },
      body: (expense: ExpenseListItem) => (
        <span className="apollo-body">{expense.vendor_address || "—"}</span>
      ),
    },
    {
      field: "date",
      header: "Date",
      style: { width: "20%" },
      body: (expense: ExpenseListItem) => (
        <span className="apollo-body">{formatExpenseDate(expense.date)}</span>
      ),
    },
    {
      field: "total_amount",
      header: "Total",
      style: { width: "15%" },
      body: (expense: ExpenseListItem) => (
        <span className="apollo-body font-medium">
          {expense.total_amount === null || expense.total_amount === undefined
            ? "—"
            : `$${expense.total_amount}`}
        </span>
      ),
    },
    {
      header: "Actions",
      style: { width: "15%" },
      body: (expense: ExpenseListItem) => (
        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={(event) => {
              event.stopPropagation();
              onDelete(expense);
            }}
            className="inline-flex items-center justify-center rounded-lg bg-red-600 px-3 py-2 text-sm font-medium text-white shadow-sm transition-all duration-200 hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-red-500 focus:ring-offset-2"
          >
            Delete
          </button>
          <button
            type="button"
            onClick={(event) => {
              event.stopPropagation();
              onView(expense);
            }}
            className="inline-flex items-center justify-center rounded-lg bg-primary-500 px-4 py-2 font-medium text-white shadow-sm transition-all duration-200 hover:bg-primary-600 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2"
          >
            View
          </button>
        </div>
      ),
    },
  ];
}

interface ExpensesMobileCardsProps extends ExpenseActions {
  data: ExpenseListItem[];
}

export function ExpensesMobileCards({ data, onView, onDelete }: ExpensesMobileCardsProps) {
  return (
    <ExpenseMobileCardsTemplate
      data={data}
      onCardClick={onView}
      emptyMessage="No expenses found"
      cardContent={(expense: ExpenseListItem) => (
        <div className="flex flex-col space-y-3">
          <div className="flex items-start justify-between">
            <div>
              <h3 className="mb-1 text-h6 apollo-heading text-primary-600">
                {expense.vendor || "Unknown Vendor"}
              </h3>
              <p className="text-body-2 apollo-subheading">{formatExpenseDate(expense.date)}</p>
            </div>
            {expense.total_amount !== null && expense.total_amount !== undefined && (
              <span className="inline-flex items-center rounded-full bg-green-100 px-3 py-1.5 text-base font-medium text-green-800">
                ${expense.total_amount}
              </span>
            )}
          </div>
          {expense.vendor_address && (
            <div className="flex justify-between text-body-2 apollo-body">
              <span>Address:</span>
              <span className="text-right">{expense.vendor_address}</span>
            </div>
          )}
        </div>
      )}
      actionButtons={(expense: ExpenseListItem) => (
        <div className="flex gap-2 pt-2" onClick={(event) => event.stopPropagation()}>
          <button
            type="button"
            onClick={() => onView(expense)}
            className="inline-flex flex-1 items-center justify-center rounded-lg bg-primary-500 px-4 py-2 font-medium text-white shadow-sm transition-all duration-200 hover:bg-primary-600 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2"
          >
            View
          </button>
          <button
            type="button"
            onClick={() => onDelete(expense)}
            className="inline-flex flex-1 items-center justify-center rounded-lg bg-red-600 px-4 py-2 font-medium text-white shadow-sm transition-all duration-200 hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-red-500 focus:ring-offset-2"
          >
            Delete
          </button>
        </div>
      )}
    />
  );
}
